import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import ajaxClient, { MaxServerError } from "../../../js/common/AjaxClient";
import valueClipboardCopyService from "@/common/ValueClipboardCopyService";

interface CreateSharedBasketResponse {
    hash: string;
}

interface SharedBasketModalProperties {
    readonly isOpen: boolean;
    readonly onClose: () => void;
}

export default function SharedBasketModal({ isOpen, onClose }: SharedBasketModalProperties): React.JSX.Element | undefined {
    const { t } = useTranslation("basket");
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [sharedBasketUrl, setSharedBasketUrl] = useState<string | undefined>(undefined);
    const [copied, setCopied] = useState<boolean>(false);
    const [modalBodyHeight, setModalBodyHeight] = useState<string | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;

        if (isOpen) {
            void Promise.resolve().then(() => {
                if (cancelled) {
                    return;
                }

                setLoading(true);
                setErrorMessage(undefined);
                setSharedBasketUrl(undefined);
            });

            ajaxClient.post<CreateSharedBasketResponse>("/SharedBasket/jsCreateSharedBasket", {})
                .then((response) => {
                    if (cancelled) {
                        return;
                    }

                    void Promise.resolve().then(() => {
                        if (cancelled) {
                            return;
                        }

                        setSharedBasketUrl(`${window.location.origin}/sdileny-kosik/${response.hash}`);
                    });
                })
                .catch((error: unknown) => {
                    if (cancelled) {
                        return;
                    }

                    void Promise.resolve().then(() => {
                        if (cancelled) {
                            return;
                        }

                        if (error instanceof MaxServerError) {
                            setErrorMessage(error.message);
                        } else {
                            setErrorMessage(t("sharedBasketOnConnectError"));
                        }
                    });
                })
                .finally(() => {
                    if (cancelled) {
                        return;
                    }

                    void Promise.resolve().then(() => {
                        if (cancelled) {
                            return;
                        }

                        setLoading(false);
                        setModalBodyHeight("215px");
                    });
                });
        } else {
            void Promise.resolve().then((): void => {
                if (cancelled) {
                    return;
                }

                setModalBodyHeight("130px");
            });
        }

        return (): void => {
            cancelled = true;
        };
    }, [isOpen, t]);

    if (!isOpen) {
        return undefined;
    }

    return (
        <Modal
            id="shared-basket-modal"
            isOpen={isOpen}
            onRequestClose={onClose}
            className="modal-dialog cart-modal-info modal-width-fix modal-shared-basket"
            overlayClassName="react-modal-backdrop modal-transition"
            closeTimeoutMS={250}
        >
            <div className="modal-content">
                <div className="modal-header empty modal-header-image">
                    <button type="button" className="close" onClick={onClose}>
                        <i className="fa-solid fa-times"></i>
                    </button>
                    <h4 className="modal-title" style={{ position: "relative", marginLeft: "100px" }}>
                        <i className="fa-solid fa-share-alt"></i> { t("sharedBasketTitle") }
                    </h4>
                </div>

                <div
                    className="modal-body shared-basket-modal-body"
                    style={{ height: modalBodyHeight }}
                >
                    <div className="row">
                        <p dangerouslySetInnerHTML={{ __html: t("sharedBasketModalInfo") }} />
                        {errorMessage !== undefined && <p className="error">{ errorMessage }</p>}

                        <div className="shared-basket-form">
                            <label htmlFor="shared-basket-url" className="shared-basket-label">
                                <b>{ t("sharedBasketLink") }</b>
                            </label>
                            {loading && (
                                <div className="shared-basket-url-result">
                                    <i className='fa-solid fa-spinner fa-spin fa-lg'></i> { t("sharedBasketCreatingLink") }
                                </div>
                            )}
                            {sharedBasketUrl !== undefined && (
                                <>
                                    <input
                                        id="shared-basket-url"
                                        type="text"
                                        value={sharedBasketUrl}
                                        readOnly
                                        className="form-control shared-basket-input"
                                        style={{ width: "100%", marginBottom: "10px" }}
                                        onFocus={(event) => event.target.select()}
                                        onClick={(event) => event.currentTarget.select()}
                                    />
                                    <button
                                        className={`btn btn-success shared-basket-button${copied ? " shared-basket-button-copied" : ""}`}
                                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                                            valueClipboardCopyService.copyValueToClipboard(sharedBasketUrl, event.target as HTMLElement);
                                            setCopied(true);
                                            setTimeout(() => {
                                                setCopied(false);
                                            }, 1500);
                                        }}
                                    >
                                        <i className={copied ? "fa-solid fa-check" : "fa-solid fa-copy"}></i> {copied ? t("sharedBasketLinkCopied") : t("sharedBasketCopyLink")}
                                    </button>
                                    <div
                                        className="shared-basket-note"
                                        dangerouslySetInnerHTML={{ __html: t("sharedBasketExpirationNote") }}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
